import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { Clock, User, Mail, Shield, Calendar, Search, ChevronLeft, ChevronRight, BarChart3 } from 'lucide-react';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
} from 'chart.js';
import { Bar, Line } from 'react-chartjs-2';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend
);

const API_BASE = process.env.REACT_APP_BACKEND_URL;

const LoginHistory = () => {
  const [loginLogs, setLoginLogs] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all'); // 'all', 'org_admin', 'member'
  const [searchQuery, setSearchQuery] = useState('');
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 100;

  useEffect(() => {
    loadLoginHistory();
  }, []);

  const loadLoginHistory = async () => {
    try {
      setLoading(true);
      const headers = { Authorization: `Bearer ${localStorage.getItem('token')}` };
      const response = await axios.get(`${API_BASE}/api/auth/all-login-logs?limit=10000000`, { headers });
      console.log('📥 Login history loaded:', response.data?.length, 'logs');
      if (response.data && response.data.length > 0) {
        console.log('📥 First log sample:', response.data[0]);
      }
      setLoginLogs(response.data || []);
    } catch (error) {
      console.error('Failed to load login history:', error);
    } finally {
      setLoading(false);
    }
  };

  const formatDate = (timestamp) => {
    if (!timestamp) return 'N/A';
    const date = new Date(timestamp);
    return date.toLocaleString('no-NO', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit'
    });
  };

  const getRoleBadge = (role) => {
    const roleConfig = {
      super_admin: { color: 'bg-purple-500/20 text-purple-400', label: 'Super Admin' },
      org_admin: { color: 'bg-blue-500/20 text-blue-400', label: 'Skyteleder' },
      member: { color: 'bg-green-500/20 text-green-400', label: 'Medlem' }
    };
    
    const config = roleConfig[role] || { color: 'bg-gray-500/20 text-gray-400', label: role };
    return (
      <span className={`px-2 py-1 rounded-full text-xs font-medium ${config.color}`}>
        {config.label}
      </span>
    );
  };

  // Filter by role and search query
  const filteredLogs = loginLogs.filter(log => {
    // Role filter
    const matchesRole = filter === 'all' || log.role === filter;
    
    // Search filter (name, email, organization, date)
    if (!searchQuery.trim()) return matchesRole;
    
    const query = searchQuery.toLowerCase();
    const matchesSearch = 
      (log.full_name || log.user_name || '').toLowerCase().includes(query) ||
      (log.email || '').toLowerCase().includes(query) ||
      (log.organization_name || '').toLowerCase().includes(query) ||
      (log.role || '').toLowerCase().includes(query) ||
      formatDate(log.login_time || log.timestamp).toLowerCase().includes(query);
    
    return matchesRole && matchesSearch;
  });

  // Pagination
  const totalPages = Math.ceil(filteredLogs.length / itemsPerPage);
  const startIndex = (currentPage - 1) * itemsPerPage;
  const endIndex = startIndex + itemsPerPage;
  const paginatedLogs = filteredLogs.slice(startIndex, endIndex);

  // Reset to page 1 when search/filter changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, filter]);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-white">Laster innloggingshistorikk...</div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col md:flex-row md:justify-between md:items-center space-y-4 md:space-y-0">
        <div>
          <h2 className="text-xl font-bold text-white">Full innloggingshistorikk</h2>
          <p className="text-gray-400 text-sm">
            Viser {paginatedLogs.length} av {filteredLogs.length} innlogginger (side {currentPage} av {totalPages || 1})
          </p>
        </div>
        
        {/* Filter */}
        <div className="flex items-center space-x-2">
          <select
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
            className="bg-gray-800 border border-gray-700 text-white rounded-lg px-4 py-2 text-sm"
          >
            <option value="all">Alle roller</option>
            <option value="org_admin">Skyteledere</option>
            <option value="member">Medlemmer</option>
            <option value="super_admin">Super Admins</option>
          </select>
        </div>
      </div>

      {/* Search Field */}
      <div className="relative">
        <div className="relative">
          <input
            type="text"
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            placeholder="Søk etter navn, e-post, organisasjon, rolle eller dato..."
            className="w-full px-4 py-3 pl-10 bg-gray-800 border border-gray-700 rounded-lg text-white placeholder-gray-400 focus:outline-none focus:border-indigo-500 focus:ring-1 focus:ring-indigo-500"
          />
          <Search className="absolute left-3 top-3.5 h-5 w-5 text-gray-400" />
          {searchQuery && (
            <button
              onClick={() => setSearchQuery('')}
              className="absolute right-3 top-3 px-2 py-1 text-gray-400 hover:text-white transition-colors"
            >
              ✕
            </button>
          )}
        </div>
        {searchQuery && (
          <p className="mt-2 text-sm text-indigo-300">
            🔍 Søker etter: "{searchQuery}"
          </p>
        )}
      </div>

      {/* Login History Table */}
      <div className="bg-gray-800 rounded-lg overflow-hidden">
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-900/50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  <div className="flex items-center space-x-2">
                    <User className="h-4 w-4" />
                    <span>Bruker</span>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  <div className="flex items-center space-x-2">
                    <Mail className="h-4 w-4" />
                    <span>E-post</span>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  <div className="flex items-center space-x-2">
                    <Shield className="h-4 w-4" />
                    <span>Rolle</span>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  <div className="flex items-center space-x-2">
                    <Calendar className="h-4 w-4" />
                    <span>Innloggingstidspunkt</span>
                  </div>
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-400 uppercase tracking-wider">
                  Organisasjon
                </th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-700">
              {paginatedLogs.length === 0 ? (
                <tr>
                  <td colSpan="5" className="px-6 py-8 text-center text-gray-400">
                    {searchQuery ? 'Ingen innlogginger matcher søket' : 'Ingen innlogginger funnet'}
                  </td>
                </tr>
              ) : (
                paginatedLogs.map((log, index) => (
                  <tr key={log._id || index} className="hover:bg-gray-700/50 transition-colors">
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center">
                        <div className="flex-shrink-0 h-10 w-10 bg-gray-700 rounded-full flex items-center justify-center">
                          <User className="h-5 w-5 text-gray-400" />
                        </div>
                        <div className="ml-4">
                          <div className="text-sm font-medium text-white">
                            {log.full_name || log.user_name || 'Ukjent bruker'}
                          </div>
                        </div>
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-300">{log.email || 'N/A'}</div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      {getRoleBadge(log.role)}
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="flex items-center text-sm text-gray-300">
                        <Clock className="h-4 w-4 mr-2 text-gray-400" />
                        {formatDate(log.login_time || log.timestamp)}
                      </div>
                    </td>
                    <td className="px-6 py-4 whitespace-nowrap">
                      <div className="text-sm text-gray-300">
                        {log.organization_name || 'N/A'}
                      </div>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Pagination */}
      {totalPages > 1 && (
        <div className="flex items-center justify-between bg-gray-800 rounded-lg p-4">
          <div className="text-sm text-gray-400">
            Viser {startIndex + 1}-{Math.min(endIndex, filteredLogs.length)} av {filteredLogs.length} innlogginger
          </div>
          
          <div className="flex items-center space-x-2">
            <button
              onClick={() => setCurrentPage(prev => Math.max(1, prev - 1))}
              disabled={currentPage === 1}
              className="px-3 py-2 bg-gray-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 transition-colors flex items-center space-x-1"
            >
              <ChevronLeft className="h-4 w-4" />
              <span>Forrige</span>
            </button>
            
            <div className="flex items-center space-x-1">
              {[...Array(Math.min(5, totalPages))].map((_, i) => {
                let pageNum;
                if (totalPages <= 5) {
                  pageNum = i + 1;
                } else if (currentPage <= 3) {
                  pageNum = i + 1;
                } else if (currentPage >= totalPages - 2) {
                  pageNum = totalPages - 4 + i;
                } else {
                  pageNum = currentPage - 2 + i;
                }
                
                return (
                  <button
                    key={pageNum}
                    onClick={() => setCurrentPage(pageNum)}
                    className={`px-3 py-2 rounded-lg transition-colors ${
                      currentPage === pageNum
                        ? 'bg-indigo-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                    }`}
                  >
                    {pageNum}
                  </button>
                );
              })}
            </div>
            
            <button
              onClick={() => setCurrentPage(prev => Math.min(totalPages, prev + 1))}
              disabled={currentPage === totalPages}
              className="px-3 py-2 bg-gray-700 text-white rounded-lg disabled:opacity-50 disabled:cursor-not-allowed hover:bg-gray-600 transition-colors flex items-center space-x-1"
            >
              <span>Neste</span>
              <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      )}

      {/* Summary Stats */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Totale innlogginger</p>
              <p className="text-2xl font-bold text-white">{loginLogs.length}</p>
            </div>
            <Clock className="h-8 w-8 text-blue-400" />
          </div>
        </div>
        
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Skyteledere</p>
              <p className="text-2xl font-bold text-white">
                {loginLogs.filter(log => log.role === 'org_admin').length}
              </p>
            </div>
            <Shield className="h-8 w-8 text-blue-400" />
          </div>
        </div>
        
        <div className="bg-gray-800 rounded-lg p-4">
          <div className="flex items-center justify-between">
            <div>
              <p className="text-sm text-gray-400">Medlemmer</p>
              <p className="text-2xl font-bold text-white">
                {loginLogs.filter(log => log.role === 'member').length}
              </p>
            </div>
            <User className="h-8 w-8 text-green-400" />
          </div>
        </div>
      </div>

      {/* Login Statistics Charts */}
      <LoginStatistics loginLogs={loginLogs} />
    </div>
  );
};

// New component for login statistics with charts
const LoginStatistics = ({ loginLogs }) => {
  // Debug: Log first login to see structure
  React.useEffect(() => {
    if (loginLogs && loginLogs.length > 0) {
      console.log('📊 First login log:', loginLogs[0]);
      console.log('📊 Total logs:', loginLogs.length);
      console.log('📊 Sample timestamp:', loginLogs[0]?.timestamp);
      console.log('📊 Timestamp type:', typeof loginLogs[0]?.timestamp);
    }
  }, [loginLogs]);

  // Calculate statistics by hour of day
  const hourlyStats = useMemo(() => {
    const hours = Array(24).fill(0);
    let processedCount = 0;
    
    loginLogs.forEach(log => {
      const timeField = log.login_time || log.timestamp;
      if (timeField) {
        try {
          const hour = new Date(timeField).getHours();
          if (!isNaN(hour)) {
            hours[hour]++;
            processedCount++;
          }
        } catch (e) {
          console.error('Error processing time:', timeField, e);
        }
      }
    });
    
    console.log('⏰ Hourly stats processed:', processedCount, 'logs');
    console.log('⏰ Hours array:', hours);
    return hours;
  }, [loginLogs]);

  // Calculate statistics by day of week
  const dailyStats = useMemo(() => {
    const days = Array(7).fill(0);
    loginLogs.forEach(log => {
      const timeField = log.login_time || log.timestamp;
      if (timeField) {
        const day = new Date(timeField).getDay();
        days[day]++;
      }
    });
    return days;
  }, [loginLogs]);

  // Calculate statistics by month
  const monthlyStats = useMemo(() => {
    const months = Array(12).fill(0);
    loginLogs.forEach(log => {
      const timeField = log.login_time || log.timestamp;
      if (timeField) {
        const month = new Date(timeField).getMonth();
        months[month]++;
      }
    });
    return months;
  }, [loginLogs]);

  // Find peak and quiet times
  const peakHour = useMemo(() => {
    const maxLogins = Math.max(...hourlyStats);
    return hourlyStats.indexOf(maxLogins);
  }, [hourlyStats]);

  const quietHour = useMemo(() => {
    const nonZeroHours = hourlyStats.filter(h => h > 0);
    if (nonZeroHours.length === 0) return 0;
    const minLogins = Math.min(...nonZeroHours);
    return hourlyStats.indexOf(minLogins);
  }, [hourlyStats]);

  const peakDay = useMemo(() => {
    const maxLogins = Math.max(...dailyStats);
    return dailyStats.indexOf(maxLogins);
  }, [dailyStats]);

  const quietDay = useMemo(() => {
    const nonZeroDays = dailyStats.filter(d => d > 0);
    if (nonZeroDays.length === 0) return 0;
    const minLogins = Math.min(...nonZeroDays);
    return dailyStats.indexOf(minLogins);
  }, [dailyStats]);

  const dayNames = ['Søndag', 'Mandag', 'Tirsdag', 'Onsdag', 'Torsdag', 'Fredag', 'Lørdag'];
  const monthNames = ['Jan', 'Feb', 'Mar', 'Apr', 'Mai', 'Jun', 'Jul', 'Aug', 'Sep', 'Okt', 'Nov', 'Des'];

  // Chart configurations
  const hourlyChartData = {
    labels: Array.from({ length: 24 }, (_, i) => `${i}:00`),
    datasets: [
      {
        label: 'Innlogginger per time',
        data: hourlyStats,
        backgroundColor: 'rgba(34, 197, 94, 0.5)',
        borderColor: 'rgba(34, 197, 94, 1)',
        borderWidth: 2,
      },
    ],
  };

  const dailyChartData = {
    labels: dayNames,
    datasets: [
      {
        label: 'Innlogginger per dag',
        data: dailyStats,
        backgroundColor: 'rgba(59, 130, 246, 0.5)',
        borderColor: 'rgba(59, 130, 246, 1)',
        borderWidth: 2,
      },
    ],
  };

  const monthlyChartData = {
    labels: monthNames,
    datasets: [
      {
        label: 'Innlogginger per måned',
        data: monthlyStats,
        backgroundColor: 'rgba(168, 85, 247, 0.5)',
        borderColor: 'rgba(168, 85, 247, 1)',
        borderWidth: 2,
        fill: true,
      },
    ],
  };

  const chartOptions = {
    responsive: true,
    maintainAspectRatio: false,
    plugins: {
      legend: {
        display: true,
        labels: {
          color: '#fff',
        },
      },
      title: {
        display: false,
      },
    },
    scales: {
      x: {
        ticks: {
          color: '#9CA3AF',
        },
        grid: {
          color: 'rgba(156, 163, 175, 0.1)',
        },
      },
      y: {
        ticks: {
          color: '#9CA3AF',
        },
        grid: {
          color: 'rgba(156, 163, 175, 0.1)',
        },
      },
    },
  };

  // Check if we have any data
  const totalLogins = loginLogs.length;
  const hasData = totalLogins > 0;

  return (
    <div className="mt-8 space-y-6">
      <div className="bg-gray-800 rounded-lg p-6">
        <div className="flex items-center space-x-3 mb-6">
          <BarChart3 className="h-6 w-6 text-yellow-500" />
          <h3 className="text-xl font-semibold text-white">
            Innloggingsstatistikk for Oppdateringsplanlegging
          </h3>
        </div>
        <p className="text-gray-400 mb-6">
          Statistikk over innloggingsmønstre for å identifisere beste tidspunkt for systemoppdateringer med minimal påvirkning på brukere.
          {hasData && <span className="text-green-400 ml-2">({totalLogins} innlogginger analysert)</span>}
        </p>

        {!hasData && (
          <div className="text-center py-12 bg-gray-900/50 rounded-lg">
            <p className="text-gray-400">Ingen innloggingsdata tilgjengelig ennå.</p>
            <p className="text-gray-500 text-sm mt-2">Statistikk vil vises når brukere begynner å logge inn.</p>
          </div>
        )}

        {hasData && (
          <>

        {/* Key Insights */}
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
          <div className="bg-green-600/20 border border-green-600/50 rounded-lg p-4">
            <p className="text-sm text-green-300 mb-1">Mest aktiv time</p>
            <p className="text-2xl font-bold text-white">{peakHour}:00</p>
            <p className="text-xs text-gray-400 mt-1">{hourlyStats[peakHour]} innlogginger</p>
          </div>
          <div className="bg-blue-600/20 border border-blue-600/50 rounded-lg p-4">
            <p className="text-sm text-blue-300 mb-1">Minst aktiv time</p>
            <p className="text-2xl font-bold text-white">{quietHour}:00</p>
            <p className="text-xs text-gray-400 mt-1">{hourlyStats[quietHour]} innlogginger</p>
          </div>
          <div className="bg-purple-600/20 border border-purple-600/50 rounded-lg p-4">
            <p className="text-sm text-purple-300 mb-1">Mest aktiv dag</p>
            <p className="text-2xl font-bold text-white">{dayNames[peakDay]}</p>
            <p className="text-xs text-gray-400 mt-1">{dailyStats[peakDay]} innlogginger</p>
          </div>
          <div className="bg-indigo-600/20 border border-indigo-600/50 rounded-lg p-4">
            <p className="text-sm text-indigo-300 mb-1">Minst aktiv dag</p>
            <p className="text-2xl font-bold text-white">{dayNames[quietDay]}</p>
            <p className="text-xs text-gray-400 mt-1">{dailyStats[quietDay]} innlogginger</p>
          </div>
        </div>

        {/* Recommendation Box */}
        <div className="bg-yellow-600/20 border border-yellow-600/50 rounded-lg p-4 mb-8">
          <h4 className="text-lg font-semibold text-yellow-300 mb-2">
            💡 Anbefaling for Oppdateringsvindu
          </h4>
          <p className="text-gray-300">
            Basert på innloggingsdata anbefales oppdateringer mellom <strong className="text-white">{quietHour}:00</strong> og{' '}
            <strong className="text-white">{(quietHour + 2) % 24}:00</strong> på <strong className="text-white">{dayNames[quietDay]}</strong> for å minimere påvirkning på brukere.
          </p>
        </div>

        {/* Charts */}
        <div className="space-y-8">
          {/* Hourly Chart */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-4">Innlogginger per Time (Døgn)</h4>
            <div className="h-64 bg-gray-900/50 rounded-lg p-4">
              <Bar data={hourlyChartData} options={chartOptions} />
            </div>
          </div>

          {/* Daily Chart */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-4">Innlogginger per Dag (Uke)</h4>
            <div className="h-64 bg-gray-900/50 rounded-lg p-4">
              <Bar data={dailyChartData} options={chartOptions} />
            </div>
          </div>

          {/* Monthly Chart */}
          <div>
            <h4 className="text-lg font-semibold text-white mb-4">Innlogginger per Måned (År)</h4>
            <div className="h-64 bg-gray-900/50 rounded-lg p-4">
              <Line data={monthlyChartData} options={chartOptions} />
            </div>
          </div>
        </div>
        </>
        )}
      </div>
    </div>
  );
};

export default LoginHistory;
